import { useRef } from "react";

export function useHistory(
  canvasRefs: React.MutableRefObject<Record<number, HTMLCanvasElement | null>>
) {
  const historyRef = useRef<Record<number, ImageData[]>>({});
  const redoRef = useRef<Record<number, ImageData[]>>({});

  const getCtx = (page: number) => {
    const canvas = canvasRefs.current[page];
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return null;
    return { canvas, ctx };
  };

  const ensureInit = (page: number) => {
    const pack = getCtx(page);
    if (!pack) return;

    historyRef.current[page] ??= [];
    redoRef.current[page] ??= [];

    if (historyRef.current[page].length === 0) {
      historyRef.current[page].push(
        pack.ctx.getImageData(0, 0, pack.canvas.width, pack.canvas.height)
      );
    }
  };

  const pushState = (page: number) => {
    const pack = getCtx(page);
    if (!pack) return;

    ensureInit(page);
    historyRef.current[page].push(
      pack.ctx.getImageData(0, 0, pack.canvas.width, pack.canvas.height)
    );
    redoRef.current[page] = [];
  };

  const undo = (page: number) => {
    ensureInit(page);
    const stack = historyRef.current[page];
    if (stack.length <= 1) return;

    const pack = getCtx(page);
    if (!pack) return;

    redoRef.current[page].push(stack.pop()!);
    pack.ctx.putImageData(stack[stack.length - 1], 0, 0);
  };

  const redo = (page: number) => {
    const redoStack = redoRef.current[page];
    if (!redoStack?.length) return;

    const pack = getCtx(page);
    if (!pack) return;

    const next = redoStack.pop()!;
    historyRef.current[page].push(next);
    pack.ctx.putImageData(next, 0, 0);
  };

  const clearPage = (page: number) => {
    const pack = getCtx(page);
    if (!pack) return;

    ensureInit(page);
    pack.ctx.clearRect(0, 0, pack.canvas.width, pack.canvas.height);
    pushState(page);
  };

  return { pushState, undo, redo, clearPage };
}
